/*
  Header cart count sync.

  Keeps every .cart-count-badge in the header truthful no matter who changed
  the cart — the theme drawer, a third-party cart app, the cart page, or a
  back/forward navigation. Reads /cart.js and writes the count into the badge.
*/

;(function () {
  const CART_PATHS = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/i

  let inFlight = null
  let queued = false
  let lastCount = null

  function badges () {
    return document.querySelectorAll('.cart-count-badge')
  }

  function render (count) {
    lastCount = count

    badges().forEach(badge => {
      badge.dataset.count = String(count)
      badge.textContent = String(count)
    })

    // Cart triggers carry the count in their accessible name.
    document.querySelectorAll('[aria-label]').forEach(elem => {
      const label = elem.getAttribute('aria-label')

      if (!elem.querySelector('.cart-count-badge')) return

      elem.setAttribute('aria-label', label.replace(/\d+\s*$/, count))
    })

    window.dispatchEvent(new CustomEvent('theme.cartCount.updated', { detail: { count } }))
  }

  async function fetchCount () {
    if (inFlight) {
      queued = true
      return inFlight
    }

    inFlight = (async () => {
      try {
        const response = await fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_url) || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        })

        if (!response.ok) return

        const cart = await response.json()

        if (typeof cart.item_count === 'number' && cart.item_count !== lastCount) {
          render(cart.item_count)
        }
      } catch (error) {
        // Offline or blocked — leave the badge as-is rather than zeroing it.
      } finally {
        inFlight = null
      }
    })()

    await inFlight

    if (queued) {
      queued = false
      return sync()
    }
  }

  let debounce = null

  function sync (delay = 120) {
    clearTimeout(debounce)
    debounce = setTimeout(fetchCount, delay)
  }

  window.syncCartCount = sync

  /* --- Seed from the rendered markup so we only repaint on real changes --- */

  function seed () {
    const badge = document.querySelector('.cart-count-badge')

    if (badge && badge.dataset.count !== undefined) {
      lastCount = Number(badge.dataset.count)
    }
  }

  /* --- 1. Anything on the page that talks to the Cart AJAX API --- */

  const nativeFetch = window.fetch

  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || ''
      const result = nativeFetch.apply(this, arguments)

      if (CART_PATHS.test(String(url).split('?')[0])) {
        result.then(() => sync()).catch(() => {})
      }

      return result
    }
  }

  const nativeOpen = XMLHttpRequest.prototype.open

  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_PATHS.test(String(url || '').split('?')[0])) {
      this.addEventListener('load', () => sync())
    }

    return nativeOpen.apply(this, arguments)
  }

  /* --- 2. Events fired by the theme and by common cart apps --- */

  const EVENTS = [
    'kt.cart.added',
    'kt.cart.changed',
    'kt.cart.reloaded',
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'upcart:cart:updated',
    'rebuy:cart.change',
    'slidecart:updated',
    'corner:cart:updated',
    'shopify:cart:update'
  ]

  EVENTS.forEach(name => {
    window.addEventListener(name, () => sync())
    document.addEventListener(name, () => sync())
  })

  /* --- 3. Coming back to the page (bfcache, checkout, cart page) --- */

  window.addEventListener('pageshow', event => {
    if (event.persisted) sync(0)
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sync(500)
  })

  /* --- 4. Late-booting cart apps: one check once everything settled --- */

  function init () {
    seed()
    sync(1500)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
